// Les statuts viennent du domaine : deux listes divergeraient un jour.
import type {
  EInvoiceStatus,
  InvoiceStatus,
  InvoiceType,
  ProjectStatus,
  QuoteStatus,
} from "@dker/core";

/**
 * Affiche l’onglet « Démo Dupont Peinture » sur l’écran de compte non relié.
 *
 * Purement cosmétique : le contrôle qui fait foi est la RPC `attach_demo_seed_tenant`
 * (migration 06), qui vérifie l’email côté Postgres et lève `demo_seed_forbidden`.
 * On ne met donc aucune adresse dans le bundle client — il est public.
 */
export const DEMO_SEED_TAB_ENABLED = import.meta.env.VITE_DEMO_SEED_TAB === "true";

/** Où revient un visiteur de la démonstration qui la quitte. */
export const LANDING_DEMO_URL = "https://www.dker.pro/#demo";

/** Agenda de prise de rendez-vous, le même que celui de la landing. */
export const DEMO_BOOKING_URL = "https://calendar.app.google/Hp1XrJo9gT2dxpnn7";

/**
 * L'écran d'arrivée d'une démonstration, lu dans `?vers=` : la landing envoie
 * directement sur le planning, les devis ou l'agent. Un chemin interne et rien
 * d'autre — `//exemple.fr` ou `https://…` feraient de la page de démo une
 * redirection ouverte vers n'importe quel site.
 */
export function demoLandingPath(raw: string | null | undefined): string {
  if (!raw || !raw.startsWith("/") || raw.startsWith("//") || raw.includes("\\")) return "/";
  return raw;
}

/** Aligné sur `auth.minimum_password_length` (supabase/config.toml). */
export const MIN_PASSWORD_LENGTH = 12;

export function validateNewPassword(password: string, confirm: string): string | null {
  if (password.length < MIN_PASSWORD_LENGTH) {
    return `Le mot de passe doit contenir au moins ${MIN_PASSWORD_LENGTH} caractères.`;
  }
  if (password !== confirm) return "Les deux mots de passe ne correspondent pas.";
  return null;
}

/** Valeurs acceptées par `public.register_new_tenant` (migration 05). */
export const ONBOARDING_TRADE_TYPES = [
  { value: "peinture", label: "Peinture" },
  { value: "maconnerie", label: "Maçonnerie" },
  { value: "plomberie", label: "Plomberie" },
  { value: "electricite", label: "Électricité" },
  { value: "menuiserie", label: "Menuiserie" },
  { value: "couverture", label: "Couverture / toiture" },
  { value: "climatisation", label: "Climatisation / chauffage" },
  { value: "paysagisme", label: "Paysagisme" },
  // Avant « Autre », et c'est le point : beaucoup d'artisans font de tout, et
  // se ranger sous « Autre corps de métier » leur ferait décrire comme un
  // défaut de classement ce qui est un positionnement.
  { value: "tous_corps_etat", label: "Tous corps d'état" },
  { value: "autre", label: "Autre corps de métier" },
] as const;

export type OnboardingTradeType = (typeof ONBOARDING_TRADE_TYPES)[number]["value"];

/** Le métier du tenant, en clair. Un métier inconnu se dit « bâtiment ». */
export const TRADE_LABELS: Record<string, string> = Object.fromEntries(
  ONBOARDING_TRADE_TYPES.map((t) => [t.value, t.label]),
);

export function tradeLabel(trade: string | null | undefined): string {
  return (trade && TRADE_LABELS[trade]) || "bâtiment";
}

/**
 * Les postes proposés sur une fiche salarié.
 *
 * Une liste et non un texte libre : trois orthographes du même poste rendent
 * l'équipe impossible à lire d'un coup d'œil, et c'est ce champ qui légende le
 * planning. Mais la liste ne **ferme** rien — « Autre » ouvre un texte libre,
 * parce qu'aucune liste ne couvrira les métiers du bâtiment et qu'un écran
 * qui refuse un poste réel est pire qu'un texte libre.
 *
 * Le champ reste du texte en base : ce sont des propositions, pas une
 * énumération. Les fiches déjà saisies gardent ce qu'elles portent.
 */
export const JOB_TITLES = [
  "Apprenti",
  "Manœuvre",
  "Ouvrier qualifié",
  "Chef d'équipe",
  "Chef de chantier",
  "Conducteur de travaux",
  "Maçon",
  "Peintre",
  "Plaquiste",
  "Carreleur",
  "Menuisier",
  "Plombier",
  "Électricien",
  "Couvreur",
  "Chauffagiste",
  "Paysagiste",
  "Chauffeur / livreur",
  "Secrétaire / assistant",
] as const;

/**
 * Les rôles proposés pour une affectation sur un chantier.
 *
 * Le patron : « il faut un menu déroulant pour attribuer les rôles des
 * employés sur les chantiers ». Une liste distincte de `JOB_TITLES`, et il
 * faut qu'elle le soit : **le poste dit un métier, le rôle sur place dit une
 * responsabilité.** Un maçon est chef d'équipe sur un chantier et compagnon
 * sur le suivant, sans que sa fiche change.
 *
 * Courte exprès. Le poste du salarié s'ajoute en tête quand il n'y figure
 * pas — c'est une vérité déjà enregistrée sur sa fiche, pas une invention de
 * cet écran — et « Autre… » reste ouvert pour le reste.
 */
export const SITE_ROLES = [
  "Chef de chantier",
  "Chef d'équipe",
  "Compagnon",
  "Apprenti",
  "Renfort",
  "Référent sécurité",
] as const;

export const QUOTE_STATUS_LABELS: Record<QuoteStatus, string> = {
  draft: "Brouillon",
  sent: "Envoyé",
  viewed: "Vu",
  accepted: "Accepté",
  rejected: "Refusé",
  expired: "Expiré",
  cancelled: "Annulé",
};

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: "Brouillon",
  sent: "Envoyée",
  partially_paid: "Acompte reçu",
  paid: "Payée",
  overdue: "En retard",
  disputed: "Litige",
  cancelled: "Annulée",
  credit_note_issued: "Avoir émis",
};

/**
 * La nature d'une facture, quand elle n'est pas celle qu'on attend.
 *
 * `invoice` est vide à dessein : c'est le cas courant, et une pastille posée
 * sur toutes les lignes ne distingue plus rien. Un acompte, une situation ou
 * un avoir, eux, se lisent très différemment — et rien, dans la liste, ne le
 * disait.
 */
export const INVOICE_TYPE_LABELS: Record<InvoiceType, string> = {
  invoice: "",
  deposit: "Acompte",
  situation: "Situation",
  credit_note: "Avoir",
};

export const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  planned: "Planifié",
  permit_pending: "Permis en attente",
  in_progress: "En cours",
  paused: "En pause",
  stopped: "À l’arrêt",
  completed: "Terminé",
  invoiced: "Facturé",
  closed: "Clôturé",
};

/** Ce que chaque statut veut dire, en une ligne, là où on le choisit. */
export const PROJECT_STATUS_HINTS: Record<ProjectStatus, string> = {
  planned: "Signé ou prévu, pas encore commencé",
  permit_pending: "On attend la mairie avant de commencer",
  in_progress: "L’équipe travaille dessus",
  paused: "Interruption voulue, on sait quand on reprend",
  stopped: "Interruption subie, reprise incertaine",
  completed: "Travaux finis, reste à facturer ou à réceptionner",
  invoiced: "Tout est facturé",
  closed: "Soldé et archivé",
};

/** Les statuts par moment du chantier — voir `ProjectStatusControl`. */
export const PROJECT_STATUS_GROUPS: { label: string; statuses: ProjectStatus[] }[] = [
  { label: "Avant", statuses: ["planned", "permit_pending"] },
  { label: "Pendant", statuses: ["in_progress", "paused", "stopped"] },
  { label: "Après", statuses: ["completed", "invoiced", "closed"] },
];

/**
 * Les motifs courants, proposés et jamais imposés : « Autre… » rouvre la
 * saisie. Une liste courte évite trois orthographes d'« intempéries » sur le
 * tableau de bord.
 */
export const STATUS_REASON_SUGGESTIONS: Partial<Record<ProjectStatus, readonly string[]>> = {
  paused: [
    "Congés",
    "Séchage, temps de prise",
    "Attente d’une livraison",
    "Attente d’un autre corps d’état",
    "Décalage demandé par le client",
  ],
  stopped: [
    "Intempéries",
    "Litige avec le client",
    "Impayé",
    "Sinistre",
    "Problème de sécurité",
    "Arrêt administratif ou inspection",
  ],
};

export const EINVOICE_STATUS_LABELS: Record<EInvoiceStatus, string> = {
  not_sent: "Non transmise",
  generating: "Génération…",
  ready: "Prête",
  submitted_to_pa: "Soumise PA",
  accepted_by_pa: "Acceptée PA",
  rejected_by_pa: "Rejetée PA",
  delivered_to_buyer: "Livrée client",
  acknowledged: "Accusée",
  refused_by_buyer: "Refusée client",
  chorus_submitted: "Soumise Chorus",
  chorus_accepted: "Acceptée Chorus",
  chorus_rejected: "Rejetée Chorus",
};

export const UNITS = [
  { value: "m²" }, { value: "ml" }, { value: "m³" },
  { value: "u" }, { value: "h" }, { value: "forfait" },
  { value: "kg" }, { value: "l" },
];

/**
 * Bucket des photos de chantier. Privé : ses objets ne s'atteignent que par
 * une URL signée (voir `lib/signedUrls.ts`).
 */
export const PHOTO_BUCKET = "photos";
